import React, { useContext, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import ProductEditView from "../sections/product/view/ProductEditView";
import ProductSyncSection from "../components/ProductSyncSection";
import ProductStatus from "../sections/receiving/ProductStatus";
import { AuthContext } from "../context/AuthProvider";
import { AppContext } from "../context/AppProvider";
import useCin7Data from "../hooks/useCin7Data";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";

const ProductEditor = () => {
  const [query, setQuery] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const [cin7Products, setCin7Products] = useState([]);
  const [shopifyProducts, setShopifyProducts] = useState([]);
  const [shopifyB2bProducts, setShopifyB2bProducts] = useState([]);

  const [selectedCin7Product, setSelectedCin7Product] = useState(null);
  const [selectedShopifyProduct, setSelectedShopifyProduct] = useState(null);
  const [selectedShopifyB2bProduct, setSelectedShopifyB2bProduct] =
    useState(null);

  const inputRef = useRef(null);

  const authContext = useContext(AuthContext);
  const appContext = useContext(AppContext);

  const { brands, categories, suppliers, unitsOfMeasure } = useCin7Data();

  const resetValues = () => {
    setQuery("");

    setCin7Products([]);
    setShopifyProducts([]);
    setShopifyB2bProducts([]);

    setSelectedCin7Product(null);
    setSelectedShopifyProduct(null);
    setSelectedShopifyB2bProduct(null);

    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.focus();
    }
  };

  const resetLists = () => {
    setCin7Products([]);
    setShopifyProducts([]);
    setShopifyB2bProducts([]);

    setSelectedCin7Product(null);
    setSelectedShopifyProduct(null);
    setSelectedShopifyB2bProduct(null);
  };

  const handleSelectShopifyProduct = (product) => {
    setSelectedShopifyProduct(product);
    setShopifyProducts([]);
  };

  const handleSelectShopifyB2bProduct = (product) => {
    setSelectedShopifyB2bProduct(product);
    setShopifyB2bProducts([]);
  };

  const searchShopifyProduct = async (cin7Id, sku) => {
    try {
      setIsLoading(true);

      const response = await fetchWithInterceptor(
        `/api/products/shopify/?sku=${sku}&id=${cin7Id}`,
        {
          method: "GET",
          headers: { "x-access-token": authContext.getToken() },
        }
      );

      setIsLoading(false);

      if (!response.ok) {
        let message = "Error searching Shopify product.";

        try {
          const errorData = await response.json();
          message = errorData?.message || message;
        } catch (error) {
          // Ignore JSON parsing errors
        }

        appContext.showMessage(message, "error");
        return;
      }

      const { products } = await response.json();

      const b2cList = products?.b2c || [];
      const b2bList = products?.b2b || [];

      // If no products found in b2c, show error message
      if (b2cList.length === 0) {
        appContext.showMessage(
          "No Shopify product found for that SKU.",
          "error"
        );
        return;
      }

      if (b2cList.length > 1) {
        setShopifyProducts(b2cList);
      } else if (b2cList.length === 1) {
        setSelectedShopifyProduct(b2cList[0]);
      }

      if (b2bList.length > 1) {
        setShopifyB2bProducts(b2bList);
      } else if (b2bList.length === 1) {
        setSelectedShopifyB2bProduct(b2bList[0]);
      } else if (b2bList.length === 0) {
        setSelectedShopifyB2bProduct(null);
      }
    } catch (error) {
      console.error("Error searching Shopify product:", error);
      appContext.showMessage("Error searching Shopify product.", "error");
      setIsLoading(false);
    }
  };

  const searchCin7Product = async (productId) => {
    setCin7Products([]);
    setShopifyProducts([]);
    setShopifyB2bProducts([]);

    setSelectedShopifyProduct(null);
    setSelectedShopifyB2bProduct(null);

    try {
      setIsLoading(true);

      const response = await fetchWithInterceptor(
        `/api/products/cin7/${productId}`,
        {
          method: "GET",
          headers: { "x-access-token": authContext.getToken() },
        }
      );

      setIsLoading(false);

      if (!response.ok) {
        appContext.showMessage("Error searching product.", "error");
        return;
      }

      const cin7Product = await response.json();

      if (!cin7Product) {
        appContext.showMessage(
          "No matches found for that product ID.",
          "error"
        );
        return;
      }

      setSelectedCin7Product(cin7Product);

      searchShopifyProduct(cin7Product.productId, cin7Product.sku);
    } catch (error) {
      console.error("Error searching product:", error);
      appContext.showMessage("Error searching product.", "error");
      setIsLoading(false);
    }
  };

  const handleProductSearch = async (searchValue) => {
    if (!searchValue.trim()) return;

    resetLists();

    try {
      setIsLoading(true);

      const response = await fetchWithInterceptor(
        `/api/products/cin7-by-identifier/${encodeURIComponent(searchValue.trim())}`,
        {
          method: "GET",
          headers: { "x-access-token": authContext.getToken() },
        }
      );

      setIsLoading(false);

      if (response.status === 404) {
        // resetValues();
        // setQuery(searchValue);
        appContext.showMessage(
          "No matches found for that barcode/sku.",
          "error"
        );
        return;
      }

      if (!response.ok) {
        appContext.showMessage("Error searching product.", "error");
        return;
      }

      const { products } = await response.json();

      if (products.length === 0) {
        // resetValues();
        // setQuery(searchValue);
        appContext.showMessage(
          "No matches found for that barcode/sku.",
          "error"
        );
        return;
      }

      if (products.length > 1) {
        setCin7Products(products);
        return;
      }

      if (products.length === 1) {
        searchCin7Product(products[0].productId);
      }
    } catch (error) {
      console.error("Error searching product:", error);
      appContext.showMessage("Error searching product.", "error");
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress size={40} />
        </Box>
      );
    }
    if (cin7Products.length > 1) {
      return (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Multiple Cin7 products found — select one:
          </Typography>
          {cin7Products.map((product) => (
            <React.Fragment key={product.productId}>
              <ListItem disablePadding>
                <ListItemButton
                  role={undefined}
                  dense
                  onClick={() => searchCin7Product(product.productId)}
                >
                  <ListItemText
                    primary={`${product.productName}`}
                    secondary={`SKU: ${product.sku} | Barcode: ${product.barcode}`}
                  />
                  <Button>Edit This Product</Button>
                </ListItemButton>
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
          <Box mt={4}>
            <Button onClick={resetValues}>New Search</Button>
          </Box>
        </Box>
      );
    }
    if (shopifyProducts.length > 1 && !selectedShopifyProduct) {
      return (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Multiple Shopify B2C products found — select one:
          </Typography>
          {shopifyProducts.map((product) => (
            <React.Fragment key={product.productId}>
              <ListItem disablePadding>
                <ListItemButton
                  dense
                  onClick={() => handleSelectShopifyProduct(product)}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Typography variant="body1" fontWeight={600}>
                          {product.title}
                        </Typography>

                        <Box
                          display="flex"
                          gap={1}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Typography variant="body2" color="textSecondary">
                            SKU: {product.variant?.sku || "UNKNOWN"}
                          </Typography>

                          <Typography variant="body2" color="textSecondary">
                            Barcode: {product.variant?.barcode || "UNKNOWN"}
                          </Typography>

                          {product.status && (
                            <ProductStatus status={product.status} />
                          )}
                        </Box>
                      </Box>
                    }
                  />
                  <Button>Edit This Product</Button>
                </ListItemButton>
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
          <Box mt={4}>
            <Button onClick={resetValues}>New Search</Button>
          </Box>
        </Box>
      );
    }
    if (shopifyB2bProducts.length > 1 && !selectedShopifyB2bProduct) {
      return (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Multiple Shopify B2B products found — select one:
          </Typography>
          {shopifyB2bProducts.map((product) => (
            <React.Fragment key={product.productId}>
              <ListItem disablePadding>
                <ListItemButton
                  dense
                  onClick={() => handleSelectShopifyB2bProduct(product)}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Typography variant="body1" fontWeight={600}>
                          {product.title}
                        </Typography>

                        <Box
                          display="flex"
                          gap={1}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Typography variant="body2" color="textSecondary">
                            SKU: {product.variant?.sku || "UNKNOWN"}
                          </Typography>

                          <Typography variant="body2" color="textSecondary">
                            Barcode: {product.variant?.barcode || "UNKNOWN"}
                          </Typography>

                          {product.status && (
                            <ProductStatus status={product.status} />
                          )}
                        </Box>
                      </Box>
                    }
                  />
                  <Button>Edit This Product</Button>
                </ListItemButton>
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
          <Box mt={4}>
            <Button onClick={resetValues}>New Search</Button>
          </Box>
        </Box>
      );
    }
    if (selectedCin7Product && selectedShopifyProduct) {
      return (
        <ProductEditView
          cin7Product={selectedCin7Product}
          shopifyProduct={{
            b2c: selectedShopifyProduct,
            b2b: selectedShopifyB2bProduct,
          }}
          brands={brands}
          categories={categories}
          suppliers={suppliers}
          unitsOfMeasure={unitsOfMeasure}
          resetValues={resetValues}
        />
      );
    }

    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <Typography variant="body1" color="textSecondary">
        Please enter a SKU or Barcode to search for a product to edit.
        </Typography>
      </Box>
    );
  };

  const renderAlert = () => {
    if (!selectedCin7Product || !selectedShopifyProduct || isLoading)
      return null;
    if (shopifyB2bProducts.length > 1) return null;

    return (
      <Alert
        severity={!!selectedShopifyB2bProduct ? "success" : "warning"}
        sx={{ mt: 2 }}
      >
        <Typography variant="body2">
          {!!selectedShopifyB2bProduct ? (
            <>
              This product was found in <strong>Cin7</strong>,{" "}
              <strong>Shopify B2C</strong>, <strong>Shopify B2B</strong>.
            </>
          ) : (
            <>
              This product was not found in <strong>Shopify B2B</strong>.
            </>
          )}
        </Typography>
      </Alert>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={2}
        >
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              lineHeight: 1.4,
              width: { sm: 300 },
              flexShrink: 0,
            }}
          >
            Enter a product's SKU or barcode to access and update its detailed information.
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0, maxWidth: 400 }}>
            <TextField
              fullWidth
              label="SKU or Barcode"
              variant="outlined"
              size="small"
              value={query}
              inputRef={inputRef}
              onChange={(e) => {
                const value = e.target.value;
                setQuery(value);
                if (value.length >= 3) {
                  handleProductSearch(value);
                }
              }}
            />
          </Box>
          <Box>
            <ProductSyncSection />
          </Box>
        </Box>
        {renderAlert()}
      </Paper>

      <Paper sx={{ p: 2 }}>{renderContent()}</Paper>
    </Box>
  );
};

export default ProductEditor;
